import {
  BMButton,
  BMCard,
  BMComponentsType,
  BMInputField,
  BMNumberStepper,
  BMPrimitives,
  BMText,
  useBMTheme,
} from "@traveloka/district-bloom";
import IcSystemCalendarIn from "@traveloka/icon-kit-web/react/IcSystemCalendarIn";
import IcSystemCalendarOut from "@traveloka/icon-kit-web/react/IcSystemCalendarOut";
import IcSystemLocationBloom from "@traveloka/icon-kit-web/react/IcSystemLocationBloom";
import IcSystemSearch from "@traveloka/icon-kit-web/react/IcSystemSearch";
import { FC, useMemo } from "react";
import { FieldErrors, useFormContext, useWatch } from "react-hook-form";
import { StyleSheet } from "react-native";
import BMView from "../../../components/View/BMView";
import { useLocalizedDateFormat } from "../../../core";
import { useMobileContentResource } from "../../../core/resource/MobileResourceProvider";
import { appendTestId } from "../../../utils";
import { Employee } from "../common/TravelerPicker/types";
import {
  EXTRA_CONTAINER_HEIGHT,
  EXTRA_FIELD_HEIGHT,
  INITIAL_CONTAINER_HEIGHT,
  INITIAL_FIELD_HEIGHT,
  MAX_ROOMS,
} from "./constants";
import GuestField from "./GuestField";
import { HotelSearchFormValues } from "./types";

type Props = {
  onSearchFocus: () => void;
  onCalendarFocus: () => void;
  onSubmitForm: (values: HotelSearchFormValues) => void;
  onErrorForm: (errors: FieldErrors<HotelSearchFormValues>) => void;
  AddEmployeeWidget?: FC<{ addSelectedEmployee: (employee: Employee) => void }>;
  userLoggedIn: {
    name: string;
    email: string;
  };
  permissions: {
    travelerPicker: {
      canBookNonEmployee: boolean;
      canBookForOwn: boolean;
      canBookForOthers: boolean;
      canCreateEmployee: boolean;
    };
  };
};

const testID = "hotel-search_form-card";

export default function SearchFormCard(props: Props) {
  const {
    permissions,
    userLoggedIn,
    AddEmployeeWidget,
    onSearchFocus,
    onCalendarFocus,
    onSubmitForm,
    onErrorForm,
  } = props;
  const {
    control,
    formState: { errors },
    setValue,
    handleSubmit,
    clearErrors,
  } = useFormContext<HotelSearchFormValues>();
  const { BMComponents } = useBMTheme();

  const [rooms, geoName, employees, adults, checkInDate, checkOutDate] =
    useWatch({
      control,
      name: [
        "rooms",
        "geoName",
        "employees",
        "nonEmployee.adults",
        "checkInDate",
        "checkOutDate",
      ],
    });

  const { CorporateHotelSearchForm: cr } = useMobileContentResource();
  const { format } = useLocalizedDateFormat();

  const MAX_ROOMS_ALLOWED = useMemo(() => {
    const numOfEmployees = employees.length + adults || 1;
    return Math.min(MAX_ROOMS, numOfEmployees);
  }, [employees, adults]);

  const hasGeoNameError = errors.geoName?.message === "true";

  const hasEmployeeError = errors.employees?.message === "true";

  const geoNameErrorMessage = hasGeoNameError ? cr.geoNameErrorMessage : "";

  const hasError = hasGeoNameError || hasEmployeeError;

  function handleSearchPressed() {
    clearErrors();
    handleSubmit(onSubmitForm, onErrorForm)();
  }

  const styles = createStyles(BMComponents);

  return (
    <BMView
      align="center"
      style={[
        styles.container,
        {
          height:
            INITIAL_CONTAINER_HEIGHT + (hasError ? EXTRA_CONTAINER_HEIGHT : 0), // extra space for error message in mobile app
        },
      ]}
    >
      <BMCard style={styles.card} layout="COMPACT" elevation="FLOAT">
        <BMText variant="titleSmall" ink="BMStyleTextInkPrimary">
          {cr.bookYourHotel}
        </BMText>
        <BMView style={styles.formSection}>
          <BMInputField
            editable={false}
            onFocus={onSearchFocus}
            LeadingIcon={IcSystemLocationBloom}
            placeholder={cr.destinationPlaceholder}
            label={cr.destination}
            value={geoName}
            error={geoNameErrorMessage}
            testID={appendTestId(testID, "destination-input")}
          />
        </BMView>
        <BMView flex row justify="between" style={styles.formSection}>
          <BMView style={styles.fieldContainer}>
            <BMInputField
              editable={false}
              onFocus={onCalendarFocus}
              LeadingIcon={IcSystemCalendarIn}
              label={cr.checkInLabel}
              value={format(checkInDate, "SHORT_MONTH")}
              testID={appendTestId(testID, "check_in-input")}
            />
          </BMView>
          <BMView style={styles.fieldContainer}>
            <BMInputField
              editable={false}
              onFocus={onCalendarFocus}
              LeadingIcon={IcSystemCalendarOut}
              label={cr.checkOutLabel}
              value={format(checkOutDate, "SHORT_MONTH")}
              testID={appendTestId(testID, "check_out-input")}
            />
          </BMView>
        </BMView>
        <BMView
          flex
          row
          align="center"
          justify="between"
          style={styles.formSection}
        >
          <BMView
            style={[
              styles.fieldContainer,
              {
                height:
                  INITIAL_FIELD_HEIGHT +
                  (hasEmployeeError ? EXTRA_FIELD_HEIGHT : 0), // extra space for error message in mobile app
              },
            ]}
          >
            <GuestField
              AddEmployeeWidget={AddEmployeeWidget}
              permissions={permissions}
              userLoggedIn={userLoggedIn}
              testID={testID}
            />
          </BMView>
          <BMView style={styles.fieldContainer}>
            <BMText
              style={styles.stepperLabel}
              variant="uiNormal"
              ink="BMStyleTextInkPrimary"
            >
              {cr.roomLabel}
            </BMText>
            <BMNumberStepper
              minValue={1}
              maxValue={MAX_ROOMS_ALLOWED}
              value={rooms}
              onChange={(value) => setValue("rooms", value)}
              testID={appendTestId(testID, "room-stepper")}
            />
          </BMView>
        </BMView>
        <BMView style={styles.formSection}>
          <BMButton
            testID={appendTestId(testID, "search-button")}
            label={cr.searchButtonText}
            onPress={handleSearchPressed}
            size="LARGE"
            stretchable
            TrailingIcon={IcSystemSearch}
            iconStyle={styles.icon}
          />
        </BMView>
      </BMCard>
    </BMView>
  );
}

const createStyles = (BMComponents: BMComponentsType) =>
  StyleSheet.create({
    container: {
      width: "100%",
      paddingHorizontal: BMPrimitives.BMSize.md,
      paddingBottom: BMPrimitives.BMSize.xl,
    },
    card: {
      width: "100%",
      height: "100%",
    },
    fieldContainer: {
      width: "47%",
    },
    formSection: {
      marginTop: BMPrimitives.BMSize.lg,
    },
    stepperLabel: {
      marginBottom: BMPrimitives.BMSize.xs,
    },
    icon: {
      color: BMComponents.BMUiButtonInkPrimary,
    },
  });
